import { SeoHead, organizationLd, webSiteLd, webPageLd } from '../seo';
import { Link } from 'react-router-dom';

export default function Contribute() {
  return (
    <>
      <SeoHead
        title="Contribute Translations — Help Parksy Reach India | Parksy"
        description="Help translate Parksy.rent into Hindi, Tamil, Telugu, Bengali, and more. Download the translation packs, learn the glossary, and submit your work."
        canonical="https://parksy.rent/contribute"
        ogImage="https://parksy.rent/og/contribute.png"
        schemas={[organizationLd(), webSiteLd(), webPageLd({ name: 'Contribute', description: 'Help translate Parksy.rent into Indian languages.', url: 'https://parksy.rent/contribute' })]}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Help translate Parksy into your language</h1>
        <p className="text-lg text-gray-600 mb-8">
          Parksy works best when it's in your language. We're starting with Hindi, Tamil, Telugu,
          and Bengali — and we need native speakers to make the translations feel natural, not
          machine-translated.
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How to contribute</h2>
          <ol className="space-y-3 text-gray-600">
            <li><strong>1. Download</strong> the translation pack for your language below.</li>
            <li><strong>2. Translate</strong> the strings in a text editor. Keep the meaning, not the literal words.</li>
            <li><strong>3. Email</strong> the file to <a href="mailto:translate@parksy.rent" className="text-parksy-600 hover:underline">translate@parksy.rent</a>, or open a pull request on GitHub.</li>
            <li><strong>4. Get credited</strong> on our contributors page once your work ships.</li>
          </ol>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Translation packs</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { code: 'hi', name: 'Hindi', label: 'हिन्दी (Hindi)' },
              { code: 'ta', name: 'Tamil', label: 'தமிழ் (Tamil)' },
              { code: 'te', name: 'Telugu', label: 'తెలుగు (Telugu)' },
              { code: 'bn', name: 'Bengali', label: 'বাংলা (Bengali)' },
            ].map((lang) => (
              <a
                key={lang.code}
                href={`/content/${lang.code}.json`}
                download
                className="block bg-white border rounded-lg p-4 hover:border-parksy-500"
              >
                <div className="font-semibold text-gray-900">{lang.label}</div>
                <div className="text-sm text-gray-500">strings.json ({lang.code}.json) + glossary</div>
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            The English source is available at <a href="/content/en.json" download className="text-parksy-600 hover:underline">/content/en.json</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Glossary</h2>
          <p className="text-gray-600 mb-3">
            Translation accuracy matters more than poetic phrasing. Use the glossary to keep
            terms consistent across the app.
          </p>
          <ul className="bg-white border rounded-lg p-4 text-sm text-gray-700 space-y-1">
            <li><strong>parking</strong> — keep as a borrowed word in Devanagari/Tamil/Telugu/Bengali scripts where natural.</li>
            <li><strong>two-wheeler</strong> — translate to the local term (e.g. दुपहिया / இருசக்கர வாகனம் / ద్విచక్ర వాాహనం / দু-চাকার).</li>
            <li><strong>monthly</strong> — translate as "per month" or "every month" in your language.</li>
            <li><strong>brokerage</strong> — translate to the local term for middleman commission.</li>
            <li><strong>match</strong> — keep the meaning of "compatible pair found", not sports.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Quality bar</h2>
          <ul className="text-gray-600 space-y-2 list-disc pl-5">
            <li>Human-translated only. No Google Translate dumps.</li>
            <li>Reviewed by a second native speaker before merging.</li>
            <li>Tested on a real Android phone in your language.</li>
          </ul>
        </section>

        <Link
          to="/contact"
          className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-5 py-2.5 rounded-lg"
        >
          Questions? Contact us →
        </Link>
      </div>
    </>
  );
}
